import type { Notification } from './hooks/useNotifications';
import { buildProjectChatHref } from '@/lib/navigation/projectChat';
import { getDecisionById } from '@/services/decisionsService';
import { decisionSourceHref } from '@/services/decisionSourceNavigation';

function metadataString(notification: Notification, key: string): string | undefined {
  const value = notification.metadata?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function rebaseChatHref(href: string, chatPath: string): string {
  if (chatPath !== '/investor/chat') return href;
  const queryIndex = href.indexOf('?');
  if (queryIndex < 0) return chatPath;
  const params = new URLSearchParams(href.slice(queryIndex + 1));
  // Investor ChatHub must never expose an internal workspace/project return path.
  params.delete('returnTo');
  return params.size ? `${chatPath}?${params.toString()}` : chatPath;
}

/** Build a room-scoped destination from provenance delivered by the server. */
export function buildNotificationChatHref(
  notification: Notification,
  chatPath = '/messages',
): string | null {
  const roomId = notification.room_id ?? metadataString(notification, 'room_id');
  if (!roomId) return null;

  const threadId = notification.thread_parent_id ?? metadataString(notification, 'thread_parent_id');
  const workspaceId = metadataString(notification, 'workspace_id');
  const canonicalHref = buildProjectChatHref({
    roomId,
    threadId,
    workspaceId: chatPath === '/investor/chat' ? null : workspaceId,
  });

  const params = new URLSearchParams(canonicalHref.split('?')[1] ?? '');
  if (notification.type === 'room_invite') params.set('invite', 'true');
  const callSessionId = notification.call_session_id
    ?? metadataString(notification, 'call_session_id');
  if (
    (notification.type === 'call_started'
      || notification.type === 'call_missed'
      || notification.type === 'call_reminder'
      || notification.type === 'decision_requested'
      || notification.type === 'decision_action_assigned'
      || notification.type === 'decision_recorded')
    && callSessionId
  ) {
    params.set('call', callSessionId);
  }

  const base = chatPath === '/investor/chat'
    ? chatPath
    : canonicalHref.split('?')[0] || chatPath;
  return `${base}?${params.toString()}`;
}

/**
 * Resolve a notification to an authorized source. Decision notifications fall
 * back to an RLS-checked source lookup when older rows lack room provenance.
 */
export async function resolveNotificationDestination(
  notification: Notification,
  chatPath = '/messages',
): Promise<string | null> {
  const directChatHref = buildNotificationChatHref(notification, chatPath);
  if (directChatHref) return directChatHref;

  const decisionId = notification.decision_id
    ?? (notification.entity_type === 'decision' ? notification.entity_id : null)
    ?? metadataString(notification, 'decision_id');
  if (!decisionId) return null;

  const decisionResult = await getDecisionById(decisionId);
  if (!decisionResult.data || decisionResult.error) return null;

  try {
    return rebaseChatHref(await decisionSourceHref(decisionResult.data), chatPath);
  } catch {
    return null;
  }
}
